import { InlineComment, ContentType } from '@amzn/awscat-pattern-library-service-model';
import arraySort from 'array-sort';

import PatternSchemaV1 from '../../template/Pattern-v1.json';
import LocalStorage from '../../common/LocalStorage';

interface InlineCommentBySection {
  section: string;
  comments: InlineComment[];
}

interface InlineCommentByThread {
  textData: string;
  comments: InlineComment[];
}

const sectionNameMap = new Map<string, string>();

const contentSections = PatternSchemaV1.definitions.contentSections.properties;
Object.keys(contentSections).forEach((sectionKey) => {
  // @ts-expect-error TS7053 - strict migration
  sectionNameMap.set(sectionKey, contentSections[sectionKey].label);
});

const patternSections = Object.keys(contentSections);

// Categorize inline comments by section
export const categorizeInlineCommentsBySection = (
  inlineComments: InlineComment[],
  contentType: ContentType,
): InlineCommentBySection[] => {
  const categorizedInlineComments: InlineCommentBySection[] = [];
  const sections: string[] = [];
  const sectionToCommentsMap = new Map<string, InlineComment[]>();
  inlineComments.forEach((inlineComment) => {
    const { path } = inlineComment;
    const section =
      // @ts-expect-error TS2345 - strict migration
      contentType === ContentType.Pattern ? (sectionNameMap.get(path[0]) ?? '') : path[0];
    // @ts-expect-error TS2345 - strict migration
    if (sectionToCommentsMap.has(section)) {
      // @ts-expect-error TS2345 - strict migration
      sectionToCommentsMap.get(section)?.push(inlineComment);
    } else {
      // @ts-expect-error TS2345 - strict migration
      sections.push(section);
      // @ts-expect-error TS2345 - strict migration
      sectionToCommentsMap.set(section, [inlineComment]);
    }
  });
  const sortedSections =
    contentType === ContentType.Pattern
      ? patternSections.filter((patternSection) =>
          sectionToCommentsMap.has(sectionNameMap.get(patternSection) ?? ''),
        )
      : sections;
  sortedSections.forEach((section) => {
    const sectionName = sectionNameMap.get(section) ?? section;
    categorizedInlineComments.push({
      section: sectionName,
      comments: sectionToCommentsMap.get(sectionName) || [],
    });
  });

  return categorizedInlineComments;
};

// Categorize inline comments by thread and sort by created time
export const categorizeCommentsByThread = (
  inlineComments: InlineComment[],
): InlineCommentByThread[] => {
  const categorizedInlineComments: InlineCommentByThread[] = [];
  const textDataArr: string[] = [];
  const textDataToCommentsMap = new Map<string, InlineComment[]>();
  inlineComments.forEach((inlineComment) => {
    const { textData } = inlineComment;
    if (textDataToCommentsMap.has(textData)) {
      textDataToCommentsMap.get(textData)?.push(inlineComment);
    } else {
      textDataArr.push(textData);
      textDataToCommentsMap.set(textData, [inlineComment]);
    }
  });
  textDataArr.forEach((textData) => {
    const comments = textDataToCommentsMap.get(textData) || [];
    arraySort(comments, ['createdAt']);
    categorizedInlineComments.push({
      textData,
      comments,
    });
  });
  return categorizedInlineComments;
};

// filter comments that the location have changed
export const filterInlineCommentsToUpdate = (
  updatedInlineComments: InlineComment[],
  allInlineComments: InlineComment[],
) => {
  if (!updatedInlineComments?.length || !allInlineComments?.length) {
    return [];
  }
  const allCommentsIdSet = new Set(allInlineComments.map((comment) => comment.commentId));
  const updatedCommentsMap = new Map<string, InlineComment>();
  updatedInlineComments.forEach((comment) => {
    updatedCommentsMap.set(comment.commentId, comment);
  });

  // filter commetns that might be deleted
  const updatedComments = updatedInlineComments.filter((comment) =>
    allCommentsIdSet.has(comment.commentId),
  );

  // add newly created comment reply into updated comments
  allInlineComments.forEach((comment) => {
    if (updatedCommentsMap.has(comment.threadId)) {
      const threadComment = updatedCommentsMap.get(comment.threadId);
      updatedComments.push({
        ...comment,
        // @ts-expect-error TS18048 - strict migration
        startOffset: threadComment.startOffset,
        // @ts-expect-error TS18048 - strict migration
        endOffset: threadComment.endOffset,
        // @ts-expect-error TS18048 - strict migration
        textData: threadComment.textData,
      });
    }
  });
  return updatedComments;
};

export const truncateTextData = (textData: string) => {
  const MAX_TEXT_DATA_LENGTH = 200;
  if (textData.length > MAX_TEXT_DATA_LENGTH) {
    return textData.slice(0, MAX_TEXT_DATA_LENGTH) + '...';
  } else {
    return textData;
  }
};

export const getFinalizingInlineComments = (inlineComments: InlineComment[]) => {
  return inlineComments.map((comment) => {
    const finalizingComment = { ...comment };
    const { finalizingStartOffset, finalizingEndOffset } = comment;
    if (finalizingStartOffset || finalizingEndOffset) {
      finalizingComment.startOffset = comment.finalizingStartOffset;
      finalizingComment.endOffset = comment.finalizingEndOffset;
      finalizingComment.textData = comment.finalizingTextData;
    }
    return finalizingComment;
  });
};

export const TECH_WRITER_VIEWER_LINK = 'content-finalizing-viewer/tech-writer';

export const updateStatesInLocalStorage = (
  contentId: string,
  comments: InlineComment[],
  sectionId: string,
) => {
  const existingUpdatedComments = LocalStorage.getUpdatedComments(contentId);
  if (!existingUpdatedComments) {
    return;
  } else {
    const commentIdToCommentMap = new Map<string, InlineComment>();
    const updatedSectionComments = existingUpdatedComments[sectionId];
    comments.forEach((comment) => commentIdToCommentMap.set(comment.commentId, comment));
    // @ts-expect-error TS7006 - strict migration
    updatedSectionComments.forEach((comment) => {
      if (commentIdToCommentMap.has(comment.commentId)) {
        // @ts-expect-error TS2532 - strict migration
        comment.state = commentIdToCommentMap.get(comment.commentId).state;
      }
    });
    LocalStorage.setUpdatedComments(contentId, updatedSectionComments, sectionId);
  }
};
